import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { lazy, Suspense } from 'react'

import { createBrowserRouter, Outlet, RouterProvider } from 'react-router'

import { ErrorBoundary } from './components/ErrorBoundary'
import Login from './components/Login'
import PageLoading from './components/PageLoading'
import ProtectedRoute from './components/ProtectedRoute'
import { WorkspaceExplorerDialogProvider } from './features/workspace/providers/WorkspaceExplorerDialogProvider'
import { SignalRProvider } from './providers/SignalRProvider'
// Lazy-loaded page components
const Workspaces = lazy(() => import('./features/workspace/workspace-list/Workspaces'))
const WorkspaceMediaList = lazy(() => import('./features/workspace/workspace-media-list/WorkspaceMediaList'))

const StoryboardProcess = lazy(() => import('./features/storyboard/StoryboardProcessPage'))
const Commercials = lazy(() => import('./features/commercials/Commercials'))

const Launcher = lazy(() => import('./features/launcher/Launcher'))
const QueueManagement = lazy(() => import('./features/queue/QueueManagement'))
const Settings = lazy(() => import('./features/settings/Settings'))
const MediaPlayground = lazy(() => import('./features/playgrounds/MediaPlayground'))

// Lazy-loaded layout — will be migrated later
const MainLayout = lazy(() => import('./components/MainLayout'))

function LazyRoute({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoading />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

const authRouter = createBrowserRouter([
  {
    element: (
      <SignalRProvider>
        <MainLayout />
      </SignalRProvider>
    ),
    children: [
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <LazyRoute>
              <Launcher />
            </LazyRoute>
          </ProtectedRoute>
        ),
      },
      
      {
        path: '/settings',
        element: (
          <ProtectedRoute>
            <LazyRoute>
              <Settings />
            </LazyRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/queue-management',
        element: (
          <ProtectedRoute>
            <LazyRoute>
              <QueueManagement />
            </LazyRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/workspace',
        element: (
          <ProtectedRoute>
            <LazyRoute>
              <WorkspaceExplorerDialogProvider>
                <Outlet />
              </WorkspaceExplorerDialogProvider>
            </LazyRoute>
          </ProtectedRoute>
        ),
        children: [
          {
            path: 'workspaces',
            element: (
              <LazyRoute>
                <Workspaces />
              </LazyRoute>
            ),
          },
          {
            path: ':id/media-list',
            element: (
              <LazyRoute>
                <WorkspaceMediaList />
              </LazyRoute>
            ),
          },
          {
            path: ':id/playgrounds/:mediaType',
            element: (
              <LazyRoute>
                <MediaPlayground />
              </LazyRoute>
            ),
          },
        ],
      },
      {
        path: '/storyboard/commercials',
        element: (
          <ProtectedRoute>
            <LazyRoute>
              <Commercials />
            </LazyRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/storyboard/commercial/:id',
        element: (
          <ProtectedRoute>
            <LazyRoute>
              <StoryboardProcess />
            </LazyRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '*',
        element: <div>NOT FOUND</div>,
      },
    ],
  },
])

const unauthRouter = createBrowserRouter([
  {
    element: <MainLayout />,
    children: [
      {
        path: '*',
        element: <Login />,
      },
    ],
  },
])

export default function AppRoutes() {
  return (
    <>
      <AuthenticatedTemplate>
        <Suspense fallback={<PageLoading />}>
          <RouterProvider router={authRouter} />
        </Suspense>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Suspense fallback={<PageLoading />}>
          <RouterProvider router={unauthRouter} />
        </Suspense>
      </UnauthenticatedTemplate>
    </>
  )
}
